import { dispatch as d3dispatch, select, json as d3json } from "d3c";
import { geoPath, geoCircle } from "d3-geo";
import d3wl from 'utils/d3-wl-util';
import { circle as wgs84circle } from './wgs84';

const dispatch = d3dispatch('mouseover', 'mousemove', 'mouseout', 'click');

export default class GeomapPolygons {
	constructor(options) {
		this.options = options;
		this._state = {
			polygonsShown: false,
			zoomScale: 1,
			lastRequestedGeoJSON: null
		};
		this.svgContainer = select(this.options.element).select('svg');
		this.layer = this.svgContainer
			.select('.geomap_zoomer')
			.append('g')
			.attr('class', 'polygons');
	}

	init() {
		d3wl.rebind(this, dispatch, 'on');
	}

	enable(enable) {
		this._state.polygonsShown = enable;
	}

	zoomed(mapState, currentZoomTransform, isZoomChanged) {
		if (!this._state.path) {
			this._state.path = mapState.path;
			this._state.projection = mapState.projection;
		}
		this._state.zoomScale = currentZoomTransform.k;
		if (isZoomChanged) {
			this.layer
				.selectAll('.geomap-polygon')
				.style('stroke-width', 1 / this._state.zoomScale);

			this.layer
				.selectAll('circle:not(.geomap-circle-fixed-radius)')
				.attr('r', this.options.polygonsConfig.pointRadius / this._state.zoomScale);
		}
	}

	drawPolygons(geojsonFeatures, callback) {
		this._state.lastRequestedGeoJSON = geojsonFeatures;
		if (geojsonFeatures === 'string') {
			d3json(geojsonFeatures).then(json => {
				if (geojsonFeatures === this._state.lastRequestedGeoJSON) {
					drawPolygons.call(this, json);
				}
			});
		} else {
			drawPolygons.call(this, {
				type: 'FeatureCollection',
				features: geojsonFeatures.map(el => ({
					type: 'Feature',
					geometry: el
				}))
			});
		}

		function drawPolygons(json) {
			const geoData = json.features;

			this.layer
				.selectAll('.geomap-polygon')
				.data(geoData)
				.enter()
				.each(drawShape.bind(this));

			this._state.bounds = this._state.path.bounds(json);
			if (typeof callback === 'function') {
				callback();
			}
		}

		function drawShape(d) {
			switch (d.geometry.type) {
				case 'Point':
					drawPoint.call(this, d.geometry.coordinates);
					break;
				case 'MultiPoint':
					d.geometry.coordinates.forEach(point => {
						drawPoint.call(this, point);
					});
					break;
				case 'LineString':
				case 'MultiLineString':
					this.layer
						.append('path')
						.attr('class', 'geomap-polygon')
						.attr('d', this._state.path(d))
						.style('stroke', this.options.polygonsConfig.primaryColor)
						.style('stroke-opacity', 1)
						.style('fill', 'transparent')
						.style('stroke-width', 1 / this._state.zoomScale);
					break;
				case 'Polygon':
				case 'MultiPolygon':
					this.layer
						.append('path')
						.attr('class', 'geomap-polygon')
						.attr('d', this._state.path(d))
						.style('stroke', this.options.polygonsConfig.primaryColor)
						.style('fill', this.options.polygonsConfig.primaryColor)
						.style('fill-opacity', this.options.polygonsConfig.primaryOpacity)
						.style('stroke-opacity', 1)
						.style('stroke-width', 1 / this._state.zoomScale);
					break;
				default:
					return;
			}
			function drawPoint(point) {
				this.layer
					.append('circle')
					.attr('r', this.options.polygonsConfig.pointRadius / this._state.zoomScale)
					.attr('cx', this._state.projection([point[0], point[1]])[0])
					.attr('cy', this._state.projection([point[0], point[1]])[1])
					.style('fill', this.options.polygonsConfig.primaryColor)
					.style('fill-opacity', this.options.polygonsConfig.pointOpacity);
			}
		}
	}

	getBounds() {
		return this._state.bounds;
	}

	clearLayer() {
		this.layer
			.selectAll('.geomap-polygon')
			.remove();

		this.layer
			.selectAll('circle')
			.remove();
	}

	drawRect(topLeft, bottomRight) {
		const tlPosition = this._state.projection(topLeft);
		const brPosition = this._state.projection(bottomRight);
		this.layer
			.insert('rect', ':first-child')
			.classed('geomap-polygon-rect', true)
			.attr('x', tlPosition[0])
			.attr('y', tlPosition[1])
			.attr('width', brPosition[0] - tlPosition[0])
			.attr('height', brPosition[1] - tlPosition[1])
			.style('fill', 'red')
			.style('fill-opacity', this.options.polygonsConfig.primaryOpacity);
	}

	drawCircle(point, radius) {
		// NOTE: d3 expects points to be an array of [longitude, latitude]!
		// calculate the size (in radians) of the given radius (in km)

		const r = wgs84circle(point[1], radius * 1000).latitude;
		const path = geoPath().projection(this._state.projection);
		const circle = geoCircle()
			.center(point)
			.radius(r);

		this.layer
			.insert('path', ':first-child')
			.classed('geomap-polygon-rect', true)
			.classed('geomap-circle-fixed-radius', true)
			.attr('d', path(circle()))
			.style('fill', this.options.updateColor)
			.style('fill-opacity', this.options.polygonsConfig.primaryOpacity);
	}

	clearRects() {
		this.layer
			.selectAll('.geomap-polygon-rect')
			.remove();
	}
}
